import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import {
  MaterialType,
  MaterialGroupType,
  MaterialGroupPropsType,
} from '../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../common/Helpers'
import { MaterialChipType } from '../Hardware/HardwareUIBox'
import MaterialGroupUIBox from './MaterialGroupUIBox'

interface IProps {
  mGroup: MaterialGroupType
  materials: MaterialType[]

  editMaterialGroup(
    id: number,
    mGroup: MaterialGroupPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  openMaterialDetails(id: number): void
}

const EditMaterial: FC<IProps> = ({ mGroup, editMaterialGroup, ...props }) => {
  const idRef = useRef(mGroup.id)

  const [mGroupProps, setMGroupProps] = useState({
    name: mGroup.name,
    displayName: mGroup.displayName,
    description: mGroup.description,
    materialsChips: props.materials
      .filter((m) => mGroup.materialsIds.includes(m.id))
      .map((m) => ({
        id: m.id,
        name: m.name,
        img: m.thumbnail,
        isGroup: false,
      })) as MaterialChipType[],
  })

  const mGroupPropsRef = useRef(mGroupProps)

  const [needSave, setNeedSave] = useState(false)
  const needSaveRef = useRef(needSave)

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    idRef.current = mGroup.id

    setMGroupProps({
      name: mGroup.name,
      displayName: mGroup.displayName,
      description: mGroup.description,
      materialsChips: props.materials
        .filter((m) => mGroup.materialsIds.includes(m.id))
        .map((m) => ({
          id: m.id,
          name: m.name,
          img: m.thumbnail,
          isGroup: false,
        })),
    })

    setNeedSave(false)
  }, [mGroup])

  useEffect(() => {
    mGroupPropsRef.current = mGroupProps
    needSaveRef.current = needSave
  }, [needSave, mGroupProps])

  useEffect(
    () => () => {
      if (needSaveRef.current) {
        if (window?.confirm('Save material group?')) {
          handleSaveClick()
        }
      }
    },
    [mGroup]
  )

  const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
    setMGroupProps({
      ...mGroupProps,
      name: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleDisplayNameChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setMGroupProps({
      ...mGroupProps,
      displayName: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const handleDescriptionChange = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    setMGroupProps({
      ...mGroupProps,
      description: event.target.value.replace(/['"]/, ''),
    })
    setNeedSave(true)
  }

  const deleteMaterialsChip = (chip: MaterialChipType) => {
    setMGroupProps({
      ...mGroupProps,
      materialsChips: mGroupProps.materialsChips.filter(
        (ch) => ch.id !== chip.id
      ),
    })

    setNeedSave(true)
  }

  const addMaterialsChip = (chip: MaterialChipType) => {
    setMGroupProps({
      ...mGroupProps,
      materialsChips: [...mGroupProps.materialsChips, chip],
    })

    setNeedSave(true)
  }

  const handleSaveClick = () => {
    const name = mGroupPropsRef.current.name
    const displayName = mGroupPropsRef.current.displayName
    const description = mGroupPropsRef.current.description
    const materialsChips = mGroupPropsRef.current.materialsChips

    if (name === '') {
      showMessage(enqueueSnackbar, 'Enter material group name')
      return
    }

    needSaveRef.current = false
    setNeedSave(false)

    const newMGroup = {
      id: idRef.current,
      name,
      displayName,
      description,
      materialsIds: materialsChips.map((chip) => chip.id),
      // editable: hardware.editable
    } as MaterialGroupType

    editMaterialGroup(idRef.current, newMGroup, enqueueSnackbar)
  }

  return (
    <MaterialGroupUIBox
      title="Edit Material Group"
      materials={props.materials}
      name={{
        value: mGroupProps.name,
        onChange: handleNameChange,
      }}
      displayName={{
        value: mGroupProps.displayName,
        onChange: handleDisplayNameChange,
      }}
      description={{
        value: mGroupProps.description,
        onChange: handleDescriptionChange,
      }}
      button={{
        text: 'Save',
        onClick: handleSaveClick,
      }}
      materialsChips={{
        chips: mGroupProps.materialsChips,
        deleteChip: deleteMaterialsChip,
        addChip: addMaterialsChip,
      }}
      needSave={needSave}
      // editable={hardware.editable === '1'}
      openMaterialDetails={props.openMaterialDetails}
    />
  )
}

export default EditMaterial
